/* ─── STAGE LAYOUT ────────────────────────────────────────────────────────
   Where everything stands, in stage pixels. Pure geometry, DOM-free.

   The section is not a diagram, so this is not trace geometry. The board is
   set into its bay on the left and STAYS there — it has to, the robot arm is
   a fixed WebGL overlay aiming at that exact point — and the sector's
   services rise through one fixed spot in front of it, one at a time.

   So the geometry needed is small and concrete:

     core / bay   PcbJourney re-derives the same numbers from the same formula
                  to aim its set-down, so these two are a contract - see
                  DROP_AIM in beats.js.
     ground       the line the service renders stand on, and the line the
                  background's horizon is placed at. One line, shared, so the
                  board and the site read as one place.
     stageX       where a service stands when it is the one being shown. Every
                  render uses this same x; what separates them is WHEN.
     rigW / rigH  the box a render is drawn in, bottom-centred on the ground.
     feed         the cable from the board out to whatever is standing at
                  stageX. Fixed, because the thing it points at is always in
                  the same place.
   ───────────────────────────────────────────────────────────────────────── */

import { DROP_AIM } from './beats';

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const r = n => Math.round(n * 100) / 100;

/* Depth rates, kept for the one strip that still travels sideways. `mid` is
   1 by definition — it is the plane the story happens on. */
/* Four rates across the frame. `deep` is the horizon and barely moves — it
   is what gives the far skyline something to be in front of, and two
   background rates is the difference between a backdrop and a distance. */
export const DEPTH = { deep: 0.14, far: 0.30, mid: 1, near: 1.72 };

/* A render's footprint in its own local coordinates. */
export const STATION_HALF = 150;
export const STATION_RISE = 236;

export function buildLayout({ width, height, count }) {
  const w = Math.max(420, width);
  const h = Math.max(280, height);
  const n = Math.max(1, count);

  /* ── the board's end of the stage ──────────────────────────────────────
     PcbJourney computes `coreR` and `coreX` with these exact expressions off
     `.zx-canvas`'s box to work out where in the world to set the board down.
     Change the constants here and the arm puts the board next to the pad
     instead of on it. */
  const coreR = clamp(Math.min(w, h) * 0.105, 42, 76);
  const coreX = clamp(w * 0.11, coreR + 26, w * 0.2);
  const midY = h * 0.5;

  const bay = {
    x: r(coreX),
    y: r(midY + coreR * DROP_AIM),
    w: r(coreR * 1.72),
    h: r(coreR * 0.66),
  };

  /* ── the ground ────────────────────────────────────────────────────────
     One line for the whole site. It sits a little below the bay so the board
     reads as raised on its own plinth, and it is pulled up off the bottom
     edge far enough that a render's base and the caption under it both stay
     on the stage. */
  const groundY = r(clamp(bay.y + h * 0.14, h * 0.60, h - 92));

  /* Kept for callers that still scale local-coordinate artwork against the
     room above the ground line. */
  /* The drawn installations are authored about STATION_RISE tall, and want to
     sit at roughly a quarter of the frame — that is where they read as a place
     you are looking at rather than a diagram pressed against the lens. The
     0.62 is what takes the available headroom down to that. */
  const stationScale = r(clamp(((groundY - 34) / STATION_RISE) * 0.62, 0.70, 1.05));

  /* ── where the story happens ───────────────────────────────────────────
     Clear of the core and its rings, then centred in what is left. */
  const laneL = r(coreX + coreR + 52);
  const stageX = r(laneL + (w - laneL) * 0.50);

  /* Spacing. Only the foreground strip uses it now, but it is also the cap
     on how wide a caption may be. */
  const gap = r(clamp((w - laneL) * 0.88, STATION_HALF * 2.1, 620));

  /* ── the box a service's render stands in ──────────────────────────────
     Bottom-centred on the ground line. Sized by HEIGHT, because the set is a
     mix of 3:2 and 1:1 renders and matching their widths would land the
     square ones half again as tall as the landscape ones. */
  const rigH = r(clamp(groundY - 52, 170, 430));
  const rigW = r(Math.min(gap * 0.94, rigH * 1.55));

  /* One entry per service. Only the length is read now — the renders share a
     single x — but it keeps the per-service bookkeeping in one place. */
  const stations = [];
  for (let i = 0; i < n; i++) {
    stations.push({ index: i, x: r(i * gap), camera: r(stageX - i * gap) });
  }

  /* ── the feed ──────────────────────────────────────────────────────────
     Board to service. It leaves the bay low and flat, dips, and rises into
     the connector on the near side of whatever is standing at stageX — flat
     at both ends, because a cable that leaves and arrives horizontally reads
     as ROUTED, where one that leaves at an angle reads as drawn. */
  const cx = r(stageX - STATION_HALF * stationScale * 0.72);
  const cy = r(groundY - 46);
  const feedD = `M ${bay.x} ${bay.y} C ${r(bay.x + (cx - bay.x) * 0.34)} ${r(bay.y + 26)}, `
              + `${r(cx - (cx - bay.x) * 0.30)} ${cy}, ${cx} ${cy}`;

  /* Length by sampling the curve, so the dash-draw is exact. Cheap: one cubic,
     once per layout. */
  const P = [
    { x: bay.x, y: bay.y },
    { x: bay.x + (cx - bay.x) * 0.34, y: bay.y + 26 },
    { x: cx - (cx - bay.x) * 0.30, y: cy },
    { x: cx, y: cy },
  ];
  let feedLen = 0;
  let prev = P[0];
  for (let i = 1; i <= 32; i++) {
    const t = i / 32, mt = 1 - t;
    const a = mt * mt * mt, b = 3 * mt * mt * t, c = 3 * mt * t * t, d = t * t * t;
    const pt = {
      x: a * P[0].x + b * P[1].x + c * P[2].x + d * P[3].x,
      y: a * P[0].y + b * P[1].y + c * P[2].y + d * P[3].y,
    };
    feedLen += Math.hypot(pt.x - prev.x, pt.y - prev.y);
    prev = pt;
  }

  return {
    width: w,
    height: h,
    core: { x: r(coreX), y: r(midY), r: r(coreR) },
    bay,
    groundY,
    stationScale,
    laneL,
    stageX,
    gap,
    rigW,
    rigH,
    stations,
    feedD,
    feedLen: r(feedLen),
    connector: { x: cx, y: cy },
    /* Captions sit under their own render, so they get the full spacing. */
    labelWidth: Math.round(Math.min(gap - 28, STATION_HALF * 1.9)),
  };
}
